// noinspection SpellCheckingInspection

import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API_URL } from "../config";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import Dropdown from "../components/ui/Dropdown";
import ActionsMenu from "../components/ui/ActionsMenu";
import AdminTableWrapper from "../components/ui/AdminTableWrapper";
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiClock, FiRefreshCw } from "react-icons/fi";
import {
  utcToLocalDateTimeInput,
  localDateTimeToUTC,
  formatSimpleDate,
} from "../utils/dateUtils";

const AdminGameManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("seasons");
  const [seasons, setSeasons] = useState([]);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal states
  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [editingGame, setEditingGame] = useState(null);

  // Form data
  const [seasonData, setSeasonData] = useState({
    name: "",
    year: "",
    startDate: "",
    endDate: "",
    isActive: false,
  });

  const [gameData, setGameData] = useState({
    seasonId: "",
    week: "",
    gameDate: "",
    homeTeamId: "",
    awayTeamId: "",
    spread: "",
    overUnder: "",
  });

  const [scoreData, setScoreData] = useState({
    homeScore: "",
    awayScore: "",
    status: "completed",
  });

  useEffect(() => {
    fetchSeasons();
    fetchGames();
    fetchTeams();
  }, []);

  const fetchSeasons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/seasons`);
      setSeasons(response.data.seasons || []);
    } catch (err) {
      setError("Failed to fetch seasons");
    } finally {
      setLoading(false);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API_URL}/games`);
      setGames(response.data.games || []);
    } catch (err) {
      setError("Failed to fetch games");
    }
  };

  const refreshGames = async () => {
    try {
      await axios.get(`${API_URL}/cron/trigger/game-scores`);
    } catch (err) {
      setError("Failed to refresh games");
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API_URL}/teams`);
      setTeams(response.data.teams || []);
    } catch (err) {
      setError("Failed to fetch teams");
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API_URL}/seasons`, seasonData);
      setSuccess("Season created successfully");
      setShowSeasonModal(false);
      setSeasonData({
        name: "",
        year: "",
        startDate: "",
        endDate: "",
        isActive: false,
      });
      await fetchSeasons();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to create season");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const gamePayload = {
        ...gameData,
        gameDate: localDateTimeToUTC(gameData.gameDate),
      };
      await axios.post(`${API_URL}/games`, gamePayload);
      setSuccess("Game created successfully");
      setShowGameModal(false);
      setGameData({
        seasonId: "",
        week: "",
        gameDate: "",
        homeTeamId: "",
        awayTeamId: "",
        spread: "",
        overUnder: "",
      });
      await fetchGames();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to create game");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm("Are you sure you want to delete this season?")) return;

    try {
      await axios.delete(`${API_URL}/seasons/${seasonId}`);
      setSuccess("Season deleted successfully");
      await fetchSeasons();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to delete season");
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;

    try {
      await axios.delete(`${API_URL}/games/${gameId}`);
      setSuccess("Game deleted successfully");
      await fetchGames();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to delete game");
    }
  };

  const handleEditSeason = async (season) => {
    setEditingSeason(season);
    setSeasonData({
      name: season.name || "",
      year: season.year.toString(),
      startDate: season.startDate.split("T")[0],
      endDate: season.endDate.split("T")[0],
      isActive: season.isActive,
    });
    setShowSeasonModal(true);
  };

  const handleUpdateSeason = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API_URL}/seasons/${editingSeason.id}`, seasonData);
      setSuccess("Season updated successfully");
      setShowSeasonModal(false);
      setEditingSeason(null);
      setSeasonData({
        name: "",
        year: "",
        startDate: "",
        endDate: "",
        isActive: false,
      });
      await fetchSeasons();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to update season");
    } finally {
      setLoading(false);
    }
  };

  const handleSetActiveSeason = async (seasonId) => {
    try {
      await axios.patch(`${API_URL}/seasons/${seasonId}/set-active`);
      setSuccess("Active season updated successfully");
      await fetchSeasons();
    } catch (err) {
      setError(
        err.response?.data?.error?.message || "Failed to set active season",
      );
    }
  };

  const handleCloseSeasonModal = () => {
    setShowSeasonModal(false);
    setEditingSeason(null);
    setSeasonData({
      name: "",
      year: "",
      startDate: "",
      endDate: "",
      isActive: false,
    });
  };

  const handleEditGame = async (game) => {
    setEditingGame(game);
    setGameData({
      seasonId: game.seasonId,
      week: game.week.toString(),
      gameDate: utcToLocalDateTimeInput(game.gameDate),
      homeTeamId: game.homeTeam.id,
      awayTeamId: game.awayTeam.id,
      spread: "",
      overUnder: "",
    });
    setShowGameModal(true);
  };

  const handleUpdateGame = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const gamePayload = {
        ...gameData,
        gameDate: localDateTimeToUTC(gameData.gameDate),
      };
      await axios.put(`${API_URL}/games/${editingGame.id}`, gamePayload);
      setSuccess("Game updated successfully");
      setShowGameModal(false);
      setEditingGame(null);
      setGameData({
        seasonId: "",
        week: "",
        gameDate: "",
        homeTeamId: "",
        awayTeamId: "",
        spread: "",
        overUnder: "",
      });
      await fetchGames();
    } catch (err) {
      setError(err.response?.data?.error?.message || "Failed to update game");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGameModal = () => {
    setShowGameModal(false);
    setEditingGame(null);
    setGameData({
      seasonId: "",
      week: "",
      gameDate: "",
      homeTeamId: "",
      awayTeamId: "",
      spread: "",
      overUnder: "",
    });
  };

  const handleEditScore = async (game) => {
    setEditingGame(game);
    setScoreData({
      homeScore: game.homeScore?.toString() || "",
      awayScore: game.awayScore?.toString() || "",
      status: game.status || "completed",
    });
    setShowScoreModal(true);
  };

  const handleUpdateScore = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/games/${editingGame.id}/score`, {
        homeScore: parseInt(scoreData.homeScore),
        awayScore: parseInt(scoreData.awayScore),
        status: scoreData.status,
      });
      setSuccess("Game score updated successfully");
      setShowScoreModal(false);
      setEditingGame(null);
      setScoreData({ homeScore: "", awayScore: "", status: "completed" });
      await fetchGames();
    } catch (err) {
      setError(
        err.response?.data?.error?.message || "Failed to update game score",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseScoreModal = () => {
    setShowScoreModal(false);
    setEditingGame(null);
    setScoreData({ homeScore: "", awayScore: "", status: "completed" });
  };

  // Auto-dismiss messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!user?.isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "seasons", label: "Seasons", icon: FiCalendar },
    { id: "games", label: "Games", icon: FiClock },
  ];

  const seasonOptions = seasons.map((season) => ({
    value: season.id,
    label: `${season.year} Season`,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Game & Season Management
          </h1>
          <p className="text-gray-600">
            Manage seasons and games for the platform
          </p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "seasons" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Seasons</h2>
            <Button onClick={() => setShowSeasonModal(true)}>
              <FiPlus className="w-4 h-4 mr-2" />
              Add Season
            </Button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <AdminTableWrapper>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {seasons.map((season) => (
                    <tr key={season.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {season.name || `${season.year} Season`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {season.year}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatSimpleDate(season.startDate)} -{" "}
                          {formatSimpleDate(season.endDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            season.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {season.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <ActionsMenu
                          actions={[
                            ...(!season.isActive
                              ? [
                                  {
                                    label: "Set as Active",
                                    icon: FiCalendar,
                                    onClick: () =>
                                      handleSetActiveSeason(season.id),
                                  },
                                ]
                              : []),
                            {
                              label: "Edit Season",
                              icon: FiEdit,
                              onClick: () => handleEditSeason(season),
                            },
                            {
                              label: "Delete Season",
                              icon: FiTrash2,
                              variant: "danger",
                              onClick: () => handleDeleteSeason(season.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrapper>
          </div>
        </div>
      )}

      {activeTab === "games" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Games</h2>
            <div className="flex gap-3">
              <Button onClick={() => refreshGames()}>
                <FiRefreshCw className="w-4 h-4 mr-2" />
                Refresh Scores
              </Button>
              <Button onClick={() => setShowGameModal(true)}>
                <FiPlus className="w-4 h-4 mr-2" />
                Add Game
              </Button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <AdminTableWrapper>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Week
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matchup
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {games.slice(0, 20).map((game) => (
                    <tr key={game.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Week {game.week}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {game.awayTeam?.abbreviation || "TBD"} @{" "}
                          {game.homeTeam?.abbreviation || "TBD"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatSimpleDate(game.gameDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {!game.homeScore || !game.awayScore
                            ? "TBD"
                            : `${game.awayScore} - ${game.homeScore}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <ActionsMenu
                          actions={[
                            {
                              label: "Edit Game",
                              icon: FiEdit,
                              onClick: () => handleEditGame(game),
                            },
                            {
                              label: "Edit Score",
                              icon: FiEdit,
                              onClick: () => handleEditScore(game),
                            },
                            {
                              label: "Delete Game",
                              icon: FiTrash2,
                              variant: "danger",
                              onClick: () => handleDeleteGame(game.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableWrapper>
          </div>
        </div>
      )}

      {/* Season Modal */}
      <Modal
        isOpen={showSeasonModal}
        onClose={handleCloseSeasonModal}
        title={editingSeason ? "Edit Season" : "Add Season"}
      >
        <form
          onSubmit={editingSeason ? handleUpdateSeason : handleCreateSeason}
          className="space-y-4"
        >
          <Input
            label="Season Name"
            value={seasonData.name}
            onChange={(e) =>
              setSeasonData({ ...seasonData, name: e.target.value })
            }
            placeholder="2025 NFL Regular Season"
            required
          />
          <Input
            label="Year"
            type="number"
            value={seasonData.year}
            onChange={(e) =>
              setSeasonData({ ...seasonData, year: e.target.value })
            }
            placeholder="2025"
            required
            min="2020"
            max="2030"
          />

          <Input
            label="Start Date"
            type="date"
            value={seasonData.startDate}
            onChange={(e) =>
              setSeasonData({ ...seasonData, startDate: e.target.value })
            }
            required
          />

          <Input
            label="End Date"
            type="date"
            value={seasonData.endDate}
            onChange={(e) =>
              setSeasonData({ ...seasonData, endDate: e.target.value })
            }
            required
          />

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={seasonData.isActive}
              onChange={(e) =>
                setSeasonData({ ...seasonData, isActive: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-900"
            >
              Set as active season
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseSeasonModal}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? editingSeason
                  ? "Updating..."
                  : "Creating..."
                : editingSeason
                  ? "Update Season"
                  : "Create Season"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Game Modal */}
      <Modal
        isOpen={showGameModal}
        onClose={handleCloseGameModal}
        title={editingGame ? "Edit Game" : "Add Game"}
      >
        <form
          onSubmit={editingGame ? handleUpdateGame : handleCreateGame}
          className="space-y-4"
        >
          <Dropdown
            label="Season"
            value={gameData.seasonId}
            onChange={(value) => setGameData({ ...gameData, seasonId: value })}
            options={seasonOptions}
            placeholder="Select a season..."
          />

          <Input
            label="Week"
            type="number"
            value={gameData.week}
            onChange={(e) => setGameData({ ...gameData, week: e.target.value })}
            placeholder="1"
            required
            min="1"
            max="22"
          />

          <Input
            label="Game Date"
            type="datetime-local"
            value={gameData.gameDate}
            onChange={(e) =>
              setGameData({ ...gameData, gameDate: e.target.value })
            }
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Away Team
              </label>
              <select
                value={gameData.awayTeamId}
                onChange={(e) =>
                  setGameData({ ...gameData, awayTeamId: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Away Team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.abbreviation} - {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Home Team
              </label>
              <select
                value={gameData.homeTeamId}
                onChange={(e) =>
                  setGameData({ ...gameData, homeTeamId: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Home Team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.abbreviation} - {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Betting Lines (Optional)
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Spread"
                type="number"
                step="0.5"
                value={gameData.spread}
                onChange={(e) =>
                  setGameData({ ...gameData, spread: e.target.value })
                }
                placeholder="e.g., -3.5"
              />
              <Input
                label="Over/Under"
                type="number"
                step="0.5"
                value={gameData.overUnder}
                onChange={(e) =>
                  setGameData({ ...gameData, overUnder: e.target.value })
                }
                placeholder="e.g., 47.5"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseGameModal}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? editingGame
                  ? "Updating..."
                  : "Creating..."
                : editingGame
                  ? "Update Game"
                  : "Create Game"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Score Modal */}
      <Modal
        isOpen={showScoreModal}
        onClose={handleCloseScoreModal}
        title="Edit Game Score"
      >
        <form onSubmit={handleUpdateScore} className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            {editingGame && (
              <div>
                <strong>
                  {editingGame.awayTeam?.abbreviation} @{" "}
                  {editingGame.homeTeam?.abbreviation}
                </strong>
                <br />
                Week {editingGame.week} -{" "}
                {formatSimpleDate(editingGame.gameDate)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Away Team Score"
              type="number"
              value={scoreData.awayScore}
              onChange={(e) =>
                setScoreData({ ...scoreData, awayScore: e.target.value })
              }
              placeholder="0"
              required
              min="0"
            />
            <Input
              label="Home Team Score"
              type="number"
              value={scoreData.homeScore}
              onChange={(e) =>
                setScoreData({ ...scoreData, homeScore: e.target.value })
              }
              placeholder="0"
              required
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Game Status
            </label>
            <select
              value={scoreData.status}
              onChange={(e) =>
                setScoreData({ ...scoreData, status: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="postponed">Postponed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseScoreModal}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update Score"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminGameManagement;
